import { useState, useCallback } from "react";
import { TOTAL_STEPS } from "./onboardConfig";

/**
 * Custom hook to manage onboarding progress based on completed step IDs.
 *
 * Usage:
 *   const progress = useOnboardProgress();
 *   progress.initFromServer(employee.onboardingProcess);
 *   progress.markStepComplete("basic_details");
 *   const pct = progress.percentage; // e.g. 22
 */
export function useOnboardProgress(totalSteps: number = TOTAL_STEPS) {
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);

    /** Seed from server data (edit mode) */
    const initFromServer = useCallback((steps: string[] | undefined | null) => {
        setCompletedSteps(Array.isArray(steps) ? [...steps] : []);
    }, []);

    /** Mark a step as complete (de-duplicates automatically) */
    const markStepComplete = useCallback((stepId: string) => {
        setCompletedSteps((prev) => {
            if (prev.includes(stepId)) return prev;
            return [...prev, stepId];
        });
    }, []);

    /** Check if a specific step is already complete */
    const isStepComplete = useCallback(
        (stepId: string) => completedSteps.includes(stepId),
        [completedSteps],
    );

    /** Overall progress: 0–100 based on total steps */
    const percentage = Math.round((completedSteps.length / totalSteps) * 100);

    return {
        completedSteps,
        initFromServer,
        markStepComplete,
        isStepComplete,
        percentage,
    };
}
